import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, computed, input, model } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { remapEchartsColors, watchBodyDarkMode$ } from '@examdojo/core/widget';
import { EChartsOption } from 'echarts';
import { LineChart, ScatterChart } from 'echarts/charts';
import { GridComponent, MarkAreaComponent, MarkLineComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { SVGRenderer } from 'echarts/renderers';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';

import type { HistogramOverlay } from '../measures/measures-highlight.util';
import type { MeasureId } from '../measures/measures.model';
import type { MeasureValues } from '../measures/measures.util';
import { DEFAULT_OGIVE_DISPLAY, type OgiveDisplayConfig, type OgiveParams } from './ogive-chart.model';
import { computeOgive, inverseInterpolateOgive } from './ogive-chart.util';

echarts.use([
  LineChart,
  ScatterChart,
  SVGRenderer,
  GridComponent,
  TooltipComponent,
  MarkLineComponent,
  MarkAreaComponent,
]);

@Component({
  selector: 'dojo-ogive-chart',
  standalone: true,
  imports: [NgxEchartsDirective],
  providers: [provideEchartsCore({ echarts })],
  templateUrl: './ogive-chart.component.html',
  styleUrl: './ogive-chart.component.scss',
  host: { class: 'block w-full h-full' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OgiveChartComponent {
  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    this.isDark = toSignal(watchBodyDarkMode$(this.document), { initialValue: false });
  }

  private readonly isDark;

  readonly params = input.required<OgiveParams>();
  readonly display = input<OgiveDisplayConfig>({});
  readonly overlay = input<HistogramOverlay>({ markLines: [], markAreas: [] });
  readonly activeMeasures = input<ReadonlySet<MeasureId>>(new Set());
  readonly measureValues = input<MeasureValues>(new Map() as MeasureValues);

  readonly showPercentage = model<boolean>(true);
  readonly initOpts = { renderer: 'svg' } as const;

  private readonly computedOgive = computed(() =>
    computeOgive(this.params().data, this.params().config, this.showPercentage()),
  );

  readonly chartOptions = computed<EChartsOption>(() => {
    const display = { ...DEFAULT_OGIVE_DISPLAY, ...this.display() };
    const isPercent = this.showPercentage();
    const yAxisLabel = display.yAxisLabel ?? (isPercent ? 'Cumulative frequency (%)' : 'Cumulative frequency');

    const { points, xMin, xMax, yMax } = this.computedOgive();
    const overlay = this.overlay();
    const activeMeasures = this.activeMeasures();

    // Build L-shaped percentile reading lines for Q1, median, Q3.
    // These start from the KNOWN cumulative y-value (25%, 50%, 75% of yMax)
    // and use inverse interpolation to find where the curve crosses.
    const percentileLines = buildPercentileLines(activeMeasures, points, xMin, yMax);

    // Collect the EXACT measure values for Q1/median/Q3 so we can filter out
    // their vertical lines from the histogram overlay (which would duplicate
    // the L-shapes). We use the exact values because the inverse-interpolated
    // x-values from the ogive may differ slightly.
    //
    // Also build a remap so mark areas (IQR shaded band) align with the
    // L-shape drop points rather than the exact measure values.
    const measureValues = this.measureValues();
    const excludeXValues = new Set<number>();
    const xValueRemap = new Map<number, number>();
    if (percentileLines.length > 0 && measureValues.size > 0) {
      const labelToId: Record<string, MeasureId> = { 'Q₁': 'q1', M: 'median', 'Q₃': 'q3' };
      for (const pl of percentileLines) {
        const id = labelToId[pl.label];
        if (id) {
          const exact = measureValues.get(id);
          if (typeof exact === 'number') {
            excludeXValues.add(exact);
            xValueRemap.set(exact, pl.xValue);
          }
        }
      }
    }

    if (points.length === 0) {
      return buildEmptyChart();
    }

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#fff',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        extraCssText: 'box-shadow: 0 2px 8px rgba(0,0,0,0.08);',
        padding: [10, 14],
        textStyle: { fontSize: 12, color: '#374151' },
        formatter: (tooltipParams) => {
          const param = (tooltipParams as unknown as Array<{ value: [number, number]; dataIndex: number }>)[0];
          if (!param) {
            return '';
          }
          const [x, y] = param.value;
          const bold = (v: string | number) => `<span style="font-weight:600;color:#111827">${v}</span>`;
          const pct = yMax > 0 ? ((y / yMax) * 100).toFixed(1) : '0';
          return [
            `<div style="margin-bottom:4px;font-weight:600;color:#6366f1">${formatEdge(x)}</div>`,
            `<div style="display:flex;justify-content:space-between;gap:16px"><span style="color:#6b7280">${yAxisLabel}</span>${bold(
              isPercent ? `${y}%` : y,
            )}</div>`,
            ...(isPercent
              ? []
              : [
                  `<div style="display:flex;justify-content:space-between;gap:16px"><span style="color:#6b7280">Cumulative %</span>${bold(
                    `${pct}%`,
                  )}</div>`,
                ]),
          ].join('');
        },
      },
      grid: {
        top: 40,
        bottom: 25,
        left: 40,
        right: 30,
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        name: display.xAxisLabel,
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: { fontSize: 12, color: '#6b7280' },
        min: xMin,
        max: xMax,
        axisTick: { show: false },
        axisLine: { lineStyle: { color: '#d1d5db' } },
        axisLabel: { fontSize: 11, color: '#6b7280', hideOverlap: true },
      },
      yAxis: {
        type: 'value',
        name: yAxisLabel,
        nameLocation: 'middle',
        nameGap: 45,
        nameTextStyle: { fontSize: 12, color: '#6b7280' },
        axisTick: { show: false },
        axisLine: { show: false },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
        axisLabel: {
          fontSize: 12,
          color: '#6b7280',
          formatter: isPercent ? (v: number) => `${v}%` : undefined,
        },
      },
      series: [
        {
          type: 'line',
          name: 'Cumulative Frequency',
          data: points,
          smooth: 0.3,
          symbol: display.showDataPoints ? 'circle' : 'none',
          symbolSize: 6,
          lineStyle: {
            color: display.lineColor ?? '#6366f1',
            width: 2,
          },
          itemStyle: {
            color: display.lineColor ?? '#6366f1',
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(99, 102, 241, 0.12)' },
              { offset: 1, color: 'rgba(99, 102, 241, 0.01)' },
            ]),
          },
        },
        buildOgiveOverlaySeries(overlay, xMin, excludeXValues, percentileLines, xValueRemap),
      ],
    };
  });

  readonly adaptedOptions = computed(() => remapEchartsColors(this.chartOptions(), this.document, this.isDark()));

  togglePercentage(): void {
    this.showPercentage.update((v) => !v);
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatEdge(value: number): string {
  if (Number.isInteger(value)) {
    return String(value);
  }
  return value.toFixed(1);
}

function buildEmptyChart(): EChartsOption {
  return {
    grid: { top: 40, bottom: 25, left: 40, right: 30, containLabel: true },
    xAxis: {
      type: 'value',
      name: 'Value',
      nameLocation: 'middle',
      nameGap: 30,
      nameTextStyle: { fontSize: 12, color: '#6b7280' },
      axisLine: { lineStyle: { color: '#d1d5db' } },
      splitLine: { show: false },
      axisLabel: { fontSize: 12, color: '#6b7280' },
    },
    yAxis: {
      type: 'value',
      show: false,
    },
    series: [],
  };
}

// ---------------------------------------------------------------------------
// Percentile L-shapes — the canonical ogive reading method
// ---------------------------------------------------------------------------

// Tailwind violet-500
const COLOR_VIOLET = '#8b5cf6';
// Tailwind teal-500
const COLOR_TEAL = '#14b8a6';

interface PercentileLine {
  readonly label: string;
  readonly yValue: number;
  readonly xValue: number;
  readonly color: string;
}

/**
 * Build L-shaped percentile reading lines.
 *
 * The correct way to read an ogive:
 *   1. Start from a known cumulative y-value (25% for Q1, 50% for median, 75% for Q3)
 *   2. Go horizontally to the curve
 *   3. Drop vertically to the x-axis to read the value
 *
 * We use inverse interpolation to find the x where the curve crosses each percentile.
 */
function buildPercentileLines(
  activeMeasures: ReadonlySet<MeasureId>,
  ogivePoints: ReadonlyArray<[number, number]>,
  xMin: number,
  yMax: number,
): PercentileLine[] {
  if (ogivePoints.length < 2 || yMax <= 0) {
    return [];
  }

  const lines: PercentileLine[] = [];

  const percentiles: Array<{ ids: MeasureId[]; fraction: number; label: string; color: string }> = [
    { ids: ['q1', 'iqr'], fraction: 0.25, label: 'Q₁', color: COLOR_VIOLET },
    { ids: ['median'], fraction: 0.5, label: 'M', color: COLOR_TEAL },
    { ids: ['q3', 'iqr'], fraction: 0.75, label: 'Q₃', color: COLOR_VIOLET },
  ];

  for (const p of percentiles) {
    if (!p.ids.some((id) => activeMeasures.has(id))) {
      continue;
    }
    const targetY = yMax * p.fraction;
    const xValue = inverseInterpolateOgive(ogivePoints, targetY);
    if (xValue !== null) {
      lines.push({ label: p.label, yValue: targetY, xValue, color: p.color });
    }
  }

  return lines;
}

// ---------------------------------------------------------------------------
// Overlay series — combines percentile L-shapes with vertical lines for rest
// ---------------------------------------------------------------------------

function buildOgiveOverlaySeries(
  overlay: HistogramOverlay,
  xMin: number,
  excludeXValues: ReadonlySet<number>,
  percentileLines: PercentileLine[],
  xValueRemap: ReadonlyMap<number, number>,
): Record<string, unknown> {
  const lineData: unknown[] = [];

  // 1. Percentile L-shapes (from known y-values via inverse interpolation)
  for (const p of percentileLines) {
    const style = { color: p.color, type: 'dashed' as const, width: 1.5 };

    // Horizontal: y-axis → curve point
    lineData.push([
      {
        coord: [xMin, p.yValue],
        symbol: 'none',
        lineStyle: style,
        label: {
          formatter: p.label,
          position: 'start',
          fontSize: 10,
          color: p.color,
        },
      },
      { coord: [p.xValue, p.yValue], symbol: 'none' },
    ]);

    // Vertical: curve point → x-axis
    lineData.push([
      { coord: [p.xValue, p.yValue], symbol: 'none', lineStyle: style },
      { coord: [p.xValue, 0], symbol: 'none' },
    ]);
  }

  // 2. Vertical-only lines for non-percentile measures (mean, mode, min, max, etc.)
  //    Skip any that overlap with L-shape x-values to avoid duplicates
  for (const l of overlay.markLines) {
    if (excludeXValues.has(l.value)) {
      continue;
    }

    lineData.push({
      xAxis: l.value,
      label: {
        formatter: l.label,
        position: 'end',
        offset: [0, -10],
        fontSize: 10,
        color: l.color,
      },
      lineStyle: {
        color: l.color,
        type: l.lineStyle,
        width: 1.5,
      },
    });
  }

  return {
    type: 'scatter',
    data: [],
    silent: true,
    symbolSize: 0,
    tooltip: { show: false },
    markLine: {
      symbol: 'none',
      animation: false,
      data: lineData,
    },
    markArea: {
      animation: false,
      data: overlay.markAreas.map((a) => [
        {
          xAxis: xValueRemap.get(a.from) ?? a.from,
          itemStyle: { color: a.color },
          label: {
            show: !!a.label,
            position: 'top',
            formatter: a.label,
            color: '#6b7280',
            fontSize: 10,
          },
        },
        {
          xAxis: xValueRemap.get(a.to) ?? a.to,
        },
      ]),
    },
  };
}
